/* Young Value Co. preview behaviour. Delegated listeners only; safe across Theme Editor section reloads. */
(() => {
  if (window.yvPreviewInit) return;
  window.yvPreviewInit = true;

  // Demo claim: never touches cart/checkout, only explains that nothing happened.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-yv-preview-claim]');
    if (!button) return;
    const root = button.closest('[data-yv-product]');
    const status = root?.querySelector('[data-yv-claim-status]');
    if (!status) return;
    status.textContent = 'Preview only. No order was placed and no number was reserved.';
    status.focus();
  });
})();

// Mobile sticky claim bar: only visible once the main claim button has scrolled out of view.
if (!customElements.get('yv-sticky-bar')) {
  customElements.define('yv-sticky-bar', class extends HTMLElement {
    connectedCallback() {
      const target = this.closest('[data-yv-product]')?.querySelector('.yv-pdp__claim');
      if (!target || !('IntersectionObserver' in window)) return;
      // Viewport extended far below: "not intersecting" only means the button has passed the top,
      // so fast scroll jumps from below to above still flip the state.
      this.observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      }, { rootMargin: '0px 0px 100000px 0px' });
      this.observer.observe(target);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  });
}

if (!customElements.get('yv-reveal')) {
  customElements.define('yv-reveal', class extends HTMLElement {
    connectedCallback() {
      if (this.onInput) return;
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      this.onInput = () => {
        const value = Math.max(0, Math.min(100, Number(this.range.value)));
        this.style.setProperty('--yv-split', `${value}%`);
        this.range.setAttribute('aria-valuetext', `${value} percent wet view`);
      };
      this.range.addEventListener('input', this.onInput);
      this.onInput();
      this.rainBtn = this.querySelector('.yv-reveal__rain-btn');
      this.onRain = () => this.rain();
      this.rainBtn?.addEventListener('click', this.onRain);
    }

    // Rain mode: rain falls over the photo while the wet state washes in, holds, then dries back.
    rain() {
      if (this.classList.contains('is-raining')) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const tween = (to, ms) => new Promise((done) => {
        const from = Number(this.range.value), t0 = performance.now();
        const step = (t) => {
          const k = reduce ? 1 : Math.min(1, (t - t0) / ms);
          this.range.value = from + (to - from) * (1 - Math.pow(1 - k, 3));
          this.onInput();
          k < 1 ? requestAnimationFrame(step) : done();
        };
        requestAnimationFrame(step);
      });
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));
      this.classList.add('is-raining');
      this.rainBtn.setAttribute('aria-pressed', 'true');
      this.range.value = 0; this.onInput();
      tween(100, 2600)
        .then(() => wait(1800))
        .then(() => { this.classList.remove('is-raining'); return wait(600); })
        .then(() => tween(0, 3200))
        .then(() => this.rainBtn.setAttribute('aria-pressed', 'false'));
    }

    disconnectedCallback() {
      this.range?.removeEventListener('input', this.onInput);
      this.rainBtn?.removeEventListener('click', this.onRain);
      this.onInput = null;
    }
  });
}

// 1 + 1 offer: add the currently selected variant plus the chosen second shell in one request.
// Pricing comes from the store's automatic "Buy 1, get 1 free" discount, not from this script.
if (!customElements.get('yv-bundle')) {
  customElements.define('yv-bundle', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-yv-bundle-add]');
      this.select = this.querySelector('[data-yv-bundle-select]');
      this.status = this.querySelector('[data-yv-bundle-status]');
      this.thumb = this.querySelector('[data-yv-bundle-thumb]');
      this.onClick = () => this.add();
      this.onChange = () => this.preview();
      this.button?.addEventListener('click', this.onClick);
      this.select?.addEventListener('change', this.onChange);
      this.preview();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.select?.removeEventListener('change', this.onChange);
    }

    preview() {
      const src = this.select?.selectedOptions[0]?.dataset.image;
      if (!this.thumb || !src) return;
      this.thumb.src = src;
      this.thumb.hidden = false;
    }

    async add() {
      const form = this.closest('.shopify-section')?.querySelector('form[action*="/cart/add"]');
      const first = form?.querySelector('[name="id"]')?.value;
      const second = this.select?.value;
      if (!first || !second) return;
      this.button.setAttribute('aria-disabled', 'true');
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(first), quantity: 1 }, { id: Number(second), quantity: 1 }] }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || this.dataset.msgError || 'Could not add to bag');
        this.status.textContent = this.dataset.msgAdded || 'Both shells were added to your bag.';
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: this.button } }));
      } catch (error) {
        this.status.textContent = error.message;
      } finally {
        this.button.removeAttribute('aria-disabled');
      }
    }
  });
}
